import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import FloatingCard from "../../../ui/FloatingCard";
import ScoringModeToggle from "../../live/components/ScoringModeToggle";
import InlinePlayerGrid from "../common/InlinePlayerGrid";
import PermissionBadge from "../common/PermissionBadge";
import usePeriodClockSync from "../common/usePeriodClockSync";
import {
  getBasketballFormat,
  getBasketballPeriodLabel,
  getBasketballPeriodEndAction,
} from "../../../../utils/sportRules";

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtTime = (s) => {
  const total = Math.max(0, Math.ceil(s));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};
const PERIOD_KEY  = (id) => `basketball_period_${id}`;
const DEPART_KEY  = (id) => `basketball_depart_${id}`;
const savePeriod  = (id, data) => { try { localStorage.setItem(PERIOD_KEY(id), JSON.stringify(data)); } catch {} };
const loadPeriod  = (id) => { try { const r = localStorage.getItem(PERIOD_KEY(id)); return r ? JSON.parse(r) : null; } catch { return null; } };
const saveDepart  = (id, ts) => { try { localStorage.setItem(DEPART_KEY(id), String(ts)); } catch {} };
const loadDepart  = (id) => { try { const r = localStorage.getItem(DEPART_KEY(id)); return r ? parseInt(r, 10) : null; } catch { return null; } };
const clearDepart = (id) => { try { localStorage.removeItem(DEPART_KEY(id)); } catch {} };

// Parse match config for basketball settings.
// FIBA default: 4 x 10-minute quarters, 5-minute overtime periods (NBA 4 x 12
// can be chosen at match creation). Values come from
// match_config.scoring_rules.{periods, period_minutes, overtime_minutes}.
const getBasketballConfig = (match) => getBasketballFormat(match);

export { getBasketballConfig };

const BasketballScoreboard = ({
  match,
  currentScore,
  isReadOnlyMode,
  connected,
  isMatchActive,
  onScoreIncrease,
  onScoreDecrease,
  onResetScore,
  onToggleTimeout,
  onCompleteMatch,
  onStartMatch,
  isTimeoutActive,
  scoringMode,
  onScoringModeChange,
  advancedSupported = false,
  announcerMuted,
  onAnnouncerToggleMute,
  onAnnouncerRepeat,
  p1Players = [],
  p2Players = [],
  onPlayerSelect,
  // Server clock (LS-B09): followed by viewers, stored by the scorer
  serverClock = null,
  onClockChange,
  onPeriodChange,
}) => {
  const matchId = match?.id;
  const { periods, periodSeconds, overtimeSeconds } = getBasketballConfig(match);
  // Whether this device had its own saved clock when the board opened
  const [hasLocalClock] = useState(() => loadPeriod(matchId) != null);

  // ── Period / timer state ───────────────────────────────────────────────────
  const [currentPeriod, setCurrentPeriodRaw] = useState(() => {
    const saved = loadPeriod(matchId);
    return saved?.currentPeriod ?? 1;
  });
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = loadPeriod(matchId);
    return saved?.timeLeft ?? periodSeconds;
  });
  const [timerRunning, setTimerRunning] = useState(false);
  const [periodEnded, setPeriodEnded] = useState(false);
  const [timerHasStarted, setTimerHasStarted] = useState(false);

  // ── Fullscreen + orientation ──────────────────────────────────────────────
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(
    () => typeof window.matchMedia === 'function'
      ? window.matchMedia('(orientation: landscape)').matches
      : false
  );

  const timerRef         = useRef(null);
  const timeLeftRef      = useRef(timeLeft);
  const matchIdRef       = useRef(matchId);
  const currentPeriodRef = useRef(currentPeriod);
  const isRunningRef          = useRef(false); // true while interval is active
  const resumeAfterTimeoutRef = useRef(false);  // remember "was running" across timeout
  timeLeftRef.current      = timeLeft;
  matchIdRef.current       = matchId;
  currentPeriodRef.current = currentPeriod;

  // Re-sync period/timeLeft once matchId resolves (async match load).
  // Also calculates elapsed time when user returned after "Keep Running & Leave".
  useEffect(() => {
    if (!matchId) return;
    const saved = loadPeriod(matchId);
    if (!saved) return;

    let resolvedTimeLeft = saved.timeLeft ?? periodSeconds;

    const depart = loadDepart(matchId);
    if (depart && !saved.timerPaused) {
      const elapsed = Math.floor((Date.now() - depart) / 1000);
      resolvedTimeLeft = Math.max(0, resolvedTimeLeft - elapsed);
      clearDepart(matchId);
      if (resolvedTimeLeft <= 0) {
        setCurrentPeriodRaw(saved.currentPeriod);
        currentPeriodRef.current = saved.currentPeriod;
        setTimeLeft(0);
        timeLeftRef.current = 0;
        setPeriodEnded(true);
        savePeriod(matchId, { ...saved, timeLeft: 0, timerPaused: true, periodEnded: true });
        return;
      }
    }

    setCurrentPeriodRaw(saved.currentPeriod);
    currentPeriodRef.current = saved.currentPeriod;
    setTimeLeft(resolvedTimeLeft);
    timeLeftRef.current = resolvedTimeLeft;
    if (saved.periodEnded) {
      setPeriodEnded(true);
    } else if (resolvedTimeLeft < periodSeconds) {
      // Clock was started for this period before we left
      setTimerHasStarted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  const setCurrentPeriod = useCallback((p) => {
    setCurrentPeriodRaw(p);
    currentPeriodRef.current = p;
    const newTime = p > periods ? overtimeSeconds : periodSeconds;
    setTimeLeft(newTime);
    timeLeftRef.current = newTime;
    setPeriodEnded(false);
    setTimerHasStarted(false);  // reset for new period
    resumeAfterTimeoutRef.current = false;
    savePeriod(matchId, { currentPeriod: p, timeLeft: newTime, periodEnded: false, timerPaused: true });
  }, [matchId, periodSeconds, overtimeSeconds, periods]);

  // Sync period to match active state
  useEffect(() => {
    if (!isMatchActive) {
      clearInterval(timerRef.current); timerRef.current = null;
      isRunningRef.current = false;
      resumeAfterTimeoutRef.current = false;
      setTimerRunning(false);
    }
  }, [isMatchActive]);

  // Timeout: pause clock without losing "wants to run" state;
  // auto-resume the period clock when timeout ends.
  useEffect(() => {
    if (isTimeoutActive) {
      // Preserve isRunningRef so we know whether to auto-resume
      resumeAfterTimeoutRef.current = isRunningRef.current;
      if (timerRef.current) {
        clearInterval(timerRef.current); timerRef.current = null;
        setTimerRunning(false);
      }
    } else if (resumeAfterTimeoutRef.current) {
      // Timeout ended and clock was running before — restart it
      resumeAfterTimeoutRef.current = false;
      if (!periodEnded) startTimer();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimeoutActive]);

  // Track device orientation for fullscreen landscape hint
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(orientation: landscape)');
    const handler = (e) => setIsLandscape(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef.current || periodEnded) return;
    isRunningRef.current = true;
    setTimerHasStarted(true);
    setTimerRunning(true);
    timerRef.current = setInterval(() => {
      const next = timeLeftRef.current - 1;
      if (next <= 0) {
        clearInterval(timerRef.current); timerRef.current = null;
        isRunningRef.current = false;
        setTimeLeft(0); timeLeftRef.current = 0;
        setTimerRunning(false); setPeriodEnded(true);
        savePeriod(matchIdRef.current, { currentPeriod: currentPeriodRef.current, timeLeft: 0, periodEnded: true, timerPaused: true });
      } else {
        setTimeLeft(next); timeLeftRef.current = next;
        if (next % 10 === 0) savePeriod(matchIdRef.current, { currentPeriod: currentPeriodRef.current, timeLeft: next, timerPaused: false });
      }
    }, 1000);
  }, [periodEnded]);

  const pauseTimer = useCallback(() => {
    clearInterval(timerRef.current); timerRef.current = null;
    isRunningRef.current = false;
    setTimerRunning(false);
    savePeriod(matchIdRef.current, { currentPeriod: currentPeriodRef.current, timeLeft: timeLeftRef.current, timerPaused: true });
  }, []);

  const startTimerRef = useRef(startTimer);
  startTimerRef.current = startTimer;
  const periodLengthSec = useCallback((p) => (p > periods ? overtimeSeconds : periodSeconds), [periods, overtimeSeconds, periodSeconds]);

  // Take the period and time left from the server clock (viewers; a scorer's fresh device)
  const applyServerClock = useCallback(({ period: p, timeLeft: t, running, viewer }) => {
    setCurrentPeriodRaw(p);
    currentPeriodRef.current = p;
    setTimeLeft(t);
    timeLeftRef.current = t;
    setPeriodEnded(t <= 0);
    setTimerHasStarted(t < periodLengthSec(p));
    if (viewer) {
      setTimerRunning(!!running);
      return;
    }
    savePeriod(matchIdRef.current, { currentPeriod: p, timeLeft: t, periodEnded: t <= 0, timerPaused: !running });
    if (running && t > 0) setTimeout(() => startTimerRef.current(), 0);
  }, [periodLengthSec]);

  // On unmount: save state. If timer was running, record a departure timestamp
  // so remount can calculate elapsed time (supports "Keep Running & Leave").
  useEffect(() => () => {
    const wasRunning = isRunningRef.current;
    clearInterval(timerRef.current);
    const id = matchIdRef.current;
    if (!id) return;
    savePeriod(id, { currentPeriod: currentPeriodRef.current, timeLeft: timeLeftRef.current, timerPaused: !wasRunning });
    if (wasRunning) saveDepart(id, Date.now());
  }, []);

  const handleNextPeriod = () => {
    if (currentPeriod >= periods) return; // would be OT — handled separately
    setCurrentPeriod(currentPeriod + 1);
  };

  // Tied at the end of regulation or of an overtime → another overtime
  // period (FIBA: 5 minutes each, repeated until there is a winner).
  const handleStartOT = () => {
    const otPeriod = Math.max(currentPeriod, periods) + 1;
    const otTime = overtimeSeconds;
    setCurrentPeriodRaw(otPeriod);
    currentPeriodRef.current = otPeriod;
    setTimeLeft(otTime);
    timeLeftRef.current = otTime;
    setPeriodEnded(false);
    setTimerHasStarted(false);
    resumeAfterTimeoutRef.current = false;
    savePeriod(matchId, { currentPeriod: otPeriod, timeLeft: otTime, periodEnded: false, timerPaused: true });
  };

  // Complete the current period/quarter early without waiting for clock
  const handleCompleteCurrentPeriod = () => {
    if (!isMatchActive) return;
    clearInterval(timerRef.current); timerRef.current = null;
    isRunningRef.current = false;
    setTimerRunning(false);
    setTimeLeft(0); timeLeftRef.current = 0;
    setPeriodEnded(true);
    savePeriod(matchId, { currentPeriod: currentPeriodRef.current, timeLeft: 0, periodEnded: true, timerPaused: true });
  };

  // Auto-start match + period timer on first score click
  const handleScoreClick = useCallback((participant, pts) => {
    const isScheduled = match?.status === 'scheduled';
    if (isScheduled && onStartMatch) {
      onStartMatch(); // optimistic: fire without await, state will reconcile
    }
    if (!timerRunning && !periodEnded) {
      startTimer();
    }
    onScoreIncrease(participant, pts);
  }, [match?.status, onStartMatch, timerRunning, periodEnded, startTimer, onScoreIncrease]);

  const periodLabel = getBasketballPeriodLabel(currentPeriod, periods);

  usePeriodClockSync({
    isReadOnlyMode,
    isMatchActive,
    serverClock,
    onClockChange,
    onPeriodChange,
    period: currentPeriod,
    periodLabel,
    periodLengthSec,
    timeLeft,
    timerRunning,
    hasLocalState: hasLocalClock,
    apply: applyServerClock,
  });

  const nextPeriodLabel = getBasketballPeriodLabel(currentPeriod + 1, periods);
  const p1Score = currentScore?.participant1 || 0;
  const p2Score = currentScore?.participant2 || 0;
  // What to offer when the clock runs out: next quarter, overtime on a tie
  // (basketball has no draws), or end the game.
  const periodEndAction = getBasketballPeriodEndAction(currentPeriod, periods, p1Score, p2Score);
  const isTiedAfterRegulation = currentPeriod >= periods && p1Score === p2Score;
  const dit = (match?.matchConfig || match?.match_config)?.drop_in_teams;
  const p1Name = dit ? (dit.team_a?.name || "Team A") : (match?.participant_1_name || match?.participant1Name || "Team 1");
  const p2Name = dit ? (dit.team_b?.name || "Team B") : (match?.participant_2_name || match?.participant2Name || "Team 2");

  // Score buttons helper — basic +1/+2/+3; advanced shows inline player grid
  const renderScoreButtons = (participant, score, players) => {
    if (isReadOnlyMode) return null;
    const addBlocked = match?.status === 'completed';
    const isAdvancedInline = scoringMode === 'advanced' && advancedSupported && !!onPlayerSelect;

    if (isAdvancedInline) {
      return (
        <div className="vb-adv-inline">
          <InlinePlayerGrid participant={participant} players={players} isDisabled={addBlocked} onPlayerSelect={onPlayerSelect} />
          <button className="vb-deduct-btn" onClick={() => onScoreDecrease(participant, 1)} disabled={!isMatchActive || score < 1}>−1</button>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "0.3rem" }}>
          {[1, 2, 3].map((pts) => (
            <button
              key={pts}
              onClick={() => handleScoreClick(participant, pts)}
              disabled={addBlocked}
              style={{ padding: "0.4rem 0.6rem", fontSize: "0.82rem", fontWeight: 700, background: "rgba(34,197,94,0.2)", border: "1px solid rgba(34,197,94,0.5)", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer", minWidth: "44px", opacity: addBlocked ? 0.4 : 1 }}
            >+{pts}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "0.3rem" }}>
          {[1, 2, 3].map((pts) => (
            <button
              key={pts}
              onClick={() => onScoreDecrease(participant, pts)}
              disabled={!isMatchActive || score < pts}
              style={{ padding: "0.3rem 0.5rem", fontSize: "0.75rem", background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.4)", color: "#f59e0b", borderRadius: "0.4rem", cursor: "pointer", minWidth: "44px", opacity: (!isMatchActive || score < pts) ? 0.4 : 1 }}
            >-{pts}</button>
          ))}
        </div>
      </div>
    );
  };

  // ── Fullscreen portal content ──────────────────────────────────────────────
  const fullscreenContent = (
    <div className="bskt-fs-overlay" role="dialog" aria-label="Fullscreen scoring">
      {/* Landscape hint on portrait mobile */}
      {!isLandscape && (
        <div className="bskt-fs-rotate-hint">
          <span>📱</span> Rotate device to landscape for the best scoring experience
        </div>
      )}

      <div className="bskt-fs-header">
        <div className="bskt-fs-header-left">
          <span className="bskt-fs-period-badge">{periodLabel}</span>
        </div>
        <div
          className="bskt-fs-clock"
          style={{ color: timeLeft <= 30 && timerRunning ? '#ef4444' : '#e2e8f0' }}
        >
          {fmtTime(timeLeft)}
        </div>
        <button
          className="bskt-fs-close"
          onClick={() => setIsFullscreen(false)}
          aria-label="Exit fullscreen"
        >
          ✕
        </button>
      </div>

      <div className="bskt-fs-scoreboard">
        {/* Team 1 */}
        <div className="bskt-fs-team">
          <div className="bskt-fs-team-name">{p1Name}</div>
          <div className="bskt-fs-score-num">{p1Score}</div>
          {!isReadOnlyMode && (
            <div className="bskt-fs-btns">
              <div className="bskt-fs-btns-row">
                {[1, 2, 3].map((pts) => (
                  <button key={pts} className="bskt-fs-score-btn bskt-fs-score-btn--add"
                    onClick={() => handleScoreClick('participant1', pts)}
                    disabled={match?.status === 'completed'}>
                    +{pts}
                  </button>
                ))}
              </div>
              <div className="bskt-fs-btns-row">
                {[1, 2, 3].map((pts) => (
                  <button key={pts} className="bskt-fs-score-btn bskt-fs-score-btn--sub"
                    onClick={() => onScoreDecrease('participant1', pts)}
                    disabled={!isMatchActive || p1Score < pts}>
                    -{pts}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bskt-fs-vs">VS</div>

        {/* Team 2 */}
        <div className="bskt-fs-team">
          <div className="bskt-fs-team-name">{p2Name}</div>
          <div className="bskt-fs-score-num">{p2Score}</div>
          {!isReadOnlyMode && (
            <div className="bskt-fs-btns">
              <div className="bskt-fs-btns-row">
                {[1, 2, 3].map((pts) => (
                  <button key={pts} className="bskt-fs-score-btn bskt-fs-score-btn--add"
                    onClick={() => handleScoreClick('participant2', pts)}
                    disabled={match?.status === 'completed'}>
                    +{pts}
                  </button>
                ))}
              </div>
              <div className="bskt-fs-btns-row">
                {[1, 2, 3].map((pts) => (
                  <button key={pts} className="bskt-fs-score-btn bskt-fs-score-btn--sub"
                    onClick={() => onScoreDecrease('participant2', pts)}
                    disabled={!isMatchActive || p2Score < pts}>
                    -{pts}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Score announcer — available to editors and viewers alike, even in fullscreen */}
      {isMatchActive && onAnnouncerToggleMute && (
        <div className="bskt-fs-controls">
          <button
            className={`bskt-fs-ctrl${announcerMuted ? '' : ' bskt-fs-ctrl--announcer-on'}`}
            onClick={onAnnouncerToggleMute}
            title={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
          >
            {announcerMuted ? '🔇' : '🔊'}
          </button>
        </div>
      )}

      {/* Quick controls */}
      {!isReadOnlyMode && (
        <div className="bskt-fs-controls">
          {/* Clock controls — hidden while a timeout is active */}
          {isMatchActive && !isTimeoutActive && !periodEnded && (
            <>
              {!timerHasStarted && (
                <button className="bskt-fs-ctrl bskt-fs-ctrl--start"
                  onClick={() => { if (match?.status === 'scheduled' && onStartMatch) onStartMatch(); startTimer(); }}>
                  ▶️ {isMatchActive ? `Start ${periodLabel}` : 'Start Match & Clock'}
                </button>
              )}
              {timerHasStarted && !timerRunning && (
                <button className="bskt-fs-ctrl bskt-fs-ctrl--start" onClick={startTimer}>
                  ▶️ Resume {periodLabel}
                </button>
              )}
              {timerRunning && (
                <button className="bskt-fs-ctrl bskt-fs-ctrl--pause" onClick={pauseTimer}>⏸️ Pause</button>
              )}
              {timerHasStarted && (
                <button className="bskt-fs-ctrl bskt-fs-ctrl--end-period" onClick={handleCompleteCurrentPeriod}>
                  🔚 End {periodLabel}
                </button>
              )}
            </>
          )}
          {isMatchActive && (
            <>
              {/* Timeout toggle — always visible */}
              {!periodEnded && (
                <button className={`bskt-fs-ctrl bskt-fs-ctrl--timeout${isTimeoutActive ? ' active' : ''}`} onClick={onToggleTimeout}>
                  {isTimeoutActive ? '▶️ End Timeout' : '⏸️ Timeout'}
                </button>
              )}
              {onScoringModeChange && (
                <button
                  className="bskt-fs-ctrl bskt-fs-ctrl--mode"
                  onClick={() => onScoringModeChange(scoringMode === 'advanced' ? 'basic' : 'advanced')}
                  title="Toggle Basic/Advanced scoring"
                >
                  {scoringMode === 'advanced' ? '📊 Adv' : '⚡ Basic'}
                </button>
              )}
              <button
                className="bskt-fs-ctrl bskt-fs-ctrl--complete"
                onClick={() => { setIsFullscreen(false); onCompleteMatch(); }}
              >
                🏁 Complete
              </button>
            </>
          )}
        </div>
      )}

      {/* Period ended prompt in fullscreen */}
      {periodEnded && isMatchActive && !isReadOnlyMode && (
        <div className="bskt-fs-period-end">
          <span>⏱️ {periodLabel} ended{isTiedAfterRegulation ? ' — Tied!' : ''}</span>
          {periodEndAction === 'next_period' ? (
            <button className="bskt-fs-ctrl" onClick={handleNextPeriod}>Start {nextPeriodLabel} →</button>
          ) : periodEndAction === 'overtime' ? (
            <button className="bskt-fs-ctrl" onClick={handleStartOT}>🔄 Start {nextPeriodLabel}</button>
          ) : (
            <button className="bskt-fs-ctrl bskt-fs-ctrl--complete" onClick={() => { setIsFullscreen(false); onCompleteMatch(); }}>🏁 End Game</button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
    <FloatingCard variant="gradient" size="large" className="score-display-websocket delay-3" glow={isMatchActive}>
      {/* Header */}
      <div className="score-header-websocket">
        <div className="score-header-top">
          <h3>🏀 Basketball — {periodLabel}</h3>
          <div className="score-header-top-right">
            {!isReadOnlyMode && onScoringModeChange && (
              <ScoringModeToggle mode={scoringMode || "basic"} onChange={onScoringModeChange} sportSupported={advancedSupported} disabled={!isMatchActive} />
            )}
            <PermissionBadge isReadOnlyMode={isReadOnlyMode} />
            <button
              className="bskt-expand-btn"
              onClick={() => setIsFullscreen(true)}
              title="Fullscreen scoring mode"
              aria-label="Expand to fullscreen scoring"
            >
              ⛶
            </button>
          </div>
        </div>

        {/* Score Announcer pill — shown to editors and viewers alike */}
        {isMatchActive && onAnnouncerToggleMute && (
          <div className="score-announcer-bar">
            <button
              className={`announcer-btn${announcerMuted ? ' muted' : ''}`}
              onClick={onAnnouncerToggleMute}
              title={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
            >
              {announcerMuted ? '🔇' : '🔊'}
            </button>
            <span className="announcer-label">Score Announcer</span>
            <button className="announcer-btn" onClick={onAnnouncerRepeat} title="Repeat last announcement">🔁</button>
          </div>
        )}

        {/* Controls row */}
        {!isReadOnlyMode && (
          <div className="live-match-quick-controls">
            {match?.status === "scheduled" && onStartMatch && (
              <button className="live-ctrl-btn live-ctrl-start" onClick={onStartMatch}>🎬 Start Match</button>
            )}
            {isMatchActive && (
              <>
                {/* Clock controls — hidden while a timeout is active */}
                {!isTimeoutActive && !periodEnded && (
                  <>
                    {!timerHasStarted && (
                      <button className="live-ctrl-btn live-ctrl-start"
                        onClick={() => { if (match?.status === 'scheduled' && onStartMatch) onStartMatch(); startTimer(); }}>
                        ▶️ Start {periodLabel}
                      </button>
                    )}
                    {timerHasStarted && !timerRunning && (
                      <button className="live-ctrl-btn live-ctrl-start" onClick={startTimer}>
                        ▶️ Resume {periodLabel}
                      </button>
                    )}
                    {timerRunning && (
                      <button className="live-ctrl-btn live-ctrl-timeout" onClick={pauseTimer}>⏸️ Pause</button>
                    )}
                    {timerHasStarted && (
                      <button className="live-ctrl-btn live-ctrl-end-period" onClick={handleCompleteCurrentPeriod}
                        title={`End ${periodLabel} now`}>🔚 End {periodLabel}</button>
                    )}
                  </>
                )}
                {/* Timeout toggle — always visible when match is active and period not ended */}
                {!periodEnded && (
                  <button className={`live-ctrl-btn live-ctrl-timeout${isTimeoutActive ? ' active' : ''}`} onClick={onToggleTimeout}>
                    {isTimeoutActive ? '▶️ End Timeout' : '⏸️ Timeout'}
                  </button>
                )}
                <button className="live-ctrl-btn live-ctrl-reset" onClick={onResetScore}>🔄 Reset</button>
                <button className="live-ctrl-btn live-ctrl-complete" onClick={() => onCompleteMatch && onCompleteMatch()}>🏁 Complete</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Period ended prompt */}
      {periodEnded && isMatchActive && !isReadOnlyMode && (
        <div style={{ display: "flex", gap: "0.75rem", padding: "0.75rem 1rem", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: "0.5rem", marginBottom: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "#f59e0b", fontWeight: 600 }}>⏱️ {periodLabel} ended — {isTiedAfterRegulation ? "Tied! No draws in basketball — play overtime. " : ""}</span>
          {periodEndAction === "next_period" ? (
            <button onClick={handleNextPeriod} style={{ padding: "0.4rem 1rem", background: "rgba(99,102,241,0.3)", border: "1px solid #6366f1", color: "#a78bfa", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              Start {nextPeriodLabel} →
            </button>
          ) : periodEndAction === "overtime" ? (
            <button onClick={handleStartOT} style={{ padding: "0.4rem 1rem", background: "rgba(245,158,11,0.25)", border: "1px solid #f59e0b", color: "#f59e0b", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              🔄 Start Overtime ({nextPeriodLabel})
            </button>
          ) : (
            <button onClick={() => onCompleteMatch && onCompleteMatch()} style={{ padding: "0.4rem 1rem", background: "rgba(239,68,68,0.25)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}>
              🏁 End Game
            </button>
          )}
        </div>
      )}

      {/* Timer display */}
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <span style={{ fontSize: "2rem", fontWeight: 800, color: timeLeft <= 30 && timerRunning ? "#ef4444" : "#e2e8f0", fontVariantNumeric: "tabular-nums" }}>
          {fmtTime(timeLeft)}
        </span>
        <span style={{ marginLeft: "0.75rem", fontSize: "0.8rem", color: "#64748b" }}>
          {Array.from({ length: periods }, (_, i) => getBasketballPeriodLabel(i + 1, periods)).join(" · ")}
          {currentPeriod > periods && ` · ${periodLabel}`}
        </span>
      </div>

      {/* Scoreboard */}
      <div className="participants-websocket">
        <div className="vb-participant-col">
          <div className="vb-team-name">{p1Name}<span className="participant-type">{match?.participant1Type === 'team' ? ' 👥 Team' : ''}</span></div>
          <div className="participant-websocket participant-1">
            <div className="participant-score-large">{p1Score}</div>
            {renderScoreButtons("participant1", p1Score, p1Players)}
          </div>
        </div>

        <div className="vs-divider-websocket">
          <div className="vs-circle-websocket">VS</div>
          <div className="vs-duration" style={{ fontSize: "0.9rem", color: "#64748b" }}>{periodLabel}</div>
        </div>

        <div className="vb-participant-col">
          <div className="vb-team-name">{p2Name}<span className="participant-type">{match?.participant2Type === 'team' ? ' 👥 Team' : ''}</span></div>
          <div className="participant-websocket participant-2">
            <div className="participant-score-large">{p2Score}</div>
            {renderScoreButtons("participant2", p2Score, p2Players)}
          </div>
        </div>
      </div>
    </FloatingCard>

    {/* Fullscreen scoring overlay */}
    {isFullscreen && ReactDOM.createPortal(fullscreenContent, document.body)}
    </>
  );
};

export default BasketballScoreboard;

